import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { updateProfile } from "../../actions/authActions";
import axios from "axios";
import { apiUrl } from "../../config/config";

import SideBar from "../../components/common/SideBar";
import AttendanceView from "../Verifier-dashboard/components/AttendanceView";
import { performLogout } from "../../utils/logout";
import "./VerifierProfile.css";

// Inline-style "Log Out" button shared by every profile page so the
// option is always visible and looks identical across roles. Uses the
// same `performLogout` helper SideBar uses, so the close-out behavior
// (verifier-attendance API + activity flush + redirect) stays in sync.
const ProfileLogoutButton = () => (
  <button
    type="button"
    onClick={(e) => {
      if (e && e.preventDefault) e.preventDefault();
      performLogout();
    }}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 18px",
      borderRadius: 8,
      border: "1px solid rgba(239,68,68,0.35)",
      background: "rgba(239,68,68,0.10)",
      color: "#ff6b6b",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      transition: "background 0.15s ease, border-color 0.15s ease",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = "rgba(239,68,68,0.18)";
      e.currentTarget.style.borderColor = "rgba(239,68,68,0.55)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "rgba(239,68,68,0.10)";
      e.currentTarget.style.borderColor = "rgba(239,68,68,0.35)";
    }}
  >
    <span aria-hidden="true">🚪</span> Log Out
  </button>
);

class VerifierProfile extends Component {
  constructor() {
    super();
    this.state = {
      // Sidebar menu — Patient list / Verifier home / Verified / QC
      menus: (function () {
        var _menus = [
          { title: "Patient List", url: "/patient-list" },
          { title: "Verifier Home", url: "/verifier-home" },
          { title: "Verified", url: "/Verification-Completed" },
          { title: "QC", url: "/qc_home" },
        ];
        // Restricted Verifier tier (type 10) only: same "Queue" link as
        // patient-list.js's sidebar — filters the Patient List table down
        // to just the patients actually assigned to this verifier.
        if (localStorage.getItem("verifiertype") === "10") {
          _menus.splice(1, 0, { title: "Queue", url: "/patient-list?queue=1" });
        }
        return _menus;
      })(),
      // Restricted menu for verifiertype === "7" (kept compatible with other pages)
      menu: [{ title: "Patient List", url: "/Partner" }],
      profileLink: "verifier-profile",
      name: "",
      phone: "",
      email: "",
      gender: "M",
      address: "",
      errors: {},
      saving: false,
      savedAt: null,

      // ── Self-service file request ───────────────────────────────
      reqCount:        1,       // how many blocks to grab
      reqNotes:        "",      // optional free-text notes
      requesting:      false,   // POST in flight
      reqError:        "",
      reqFlash:        "",      // green message after a successful local request
      reqCrossServer:  "",      // set when the assignment landed on a DIFFERENT server

      // ── Today's verified patients (patient id / name / file count) ──
      verifiedToday:        [],
      verifiedTodayLoading: false,
      verifiedTodayError:   "",
    };
  }

  componentDidMount() {
    axios
      .get(apiUrl + "api/users/profile")
      .then((res) => {
        this.setState({
          name: res.data.name || "",
          phone: res.data.phone || "",
          email: res.data.email || "",
          gender: res.data.gender || "M",
          address: res.data.address || "",
          id: res.data._id,
        });
      })
      .catch(() => {});

    this.loadVerifiedToday();

    // Re-fetch on tab focus so the list/counts roll over to the new day
    // (and pick up anything verified elsewhere) without needing a manual
    // reload — same pattern AttendanceView.js uses.
    this.onFocusRefresh = () => this.loadVerifiedToday();
    window.addEventListener("focus", this.onFocusRefresh);
  }

  componentWillUnmount() {
    if (this.onFocusRefresh) {
      window.removeEventListener("focus", this.onFocusRefresh);
    }
  }

  // Fetch the list of patients THIS verifier verified today, with file
  // counts. Backend route is GET /api/users/verifierAttendance/patients
  // — scoped by verifierId + today's date (IST), same source the
  // attendance dashboard uses.
  loadVerifiedToday = () => {
    var verifierId =
      typeof window !== "undefined" ? localStorage.getItem("verifierid") : "";
    if (!verifierId) {
      this.setState({ verifiedTodayError: "No verifier ID in session." });
      return;
    }
    var pad = function (n) { return String(n).padStart(2, "0"); };
    var now = new Date(Date.now() + (5 * 60 + 30) * 60 * 1000); // IST
    var todayStr =
      now.getUTCFullYear() + "-" + pad(now.getUTCMonth() + 1) + "-" + pad(now.getUTCDate());

    this.setState({ verifiedTodayLoading: true, verifiedTodayError: "" });
    axios
      .get(
        apiUrl + "api/users/verifierAttendance/patients" +
        "?verifierId=" + encodeURIComponent(verifierId) +
        "&date=" + todayStr +
        "&_t=" + Date.now()
      )
      .then((res) => {
        var data = res.data || {};
        var list = Array.isArray(data.patients) ? data.patients : [];
        this.setState({ verifiedToday: list, verifiedTodayLoading: false });
      })
      .catch((err) => {
        this.setState({
          verifiedTodayLoading: false,
          verifiedTodayError:
            (err.response && err.response.data && err.response.data.error)
            || err.message || "Failed to load today's verified patients",
        });
      });
  };

  // Submit a self-service file request. Backend grabs the oldest N
  // unassigned, in-queue patients and adds them to this verifier's
  // `patients` array, then returns the assigned rows so the UI can
  // append them without a second round-trip.
  requestFiles = async () => {
    var count = Math.max(1, Math.min(20, Number(this.state.reqCount) || 1));
    var notes = String(this.state.reqNotes || "").slice(0, 500);
    this.setState({ requesting: true, reqError: "", reqFlash: "" });
    try {
      var res = await axios.post(apiUrl + "api/users/requestAssignment", {
        count: count,
        notes: notes,
      });
      var data = res.data || {};
      var assigned = Array.isArray(data.assigned) ? data.assigned : [];
      if (!assigned.length) {
        this.setState({
          requesting: false,
          reqFlash:    "",
          reqCrossServer: "",
          reqError:    data.message || "No patients to verify.",
        });
        return;
      }
      if (data.crossServer) {
        // Assigned on a DIFFERENT server than this one — nothing landed in
        // THIS server's queue, so make the "go elsewhere" message
        // impossible to miss.
        var crossNamedList = assigned
          .slice(0, 5)
          .map(function (p) {
            var label = p.device_id != null ? String(p.device_id) : String(p._id);
            return p.name ? label + " (" + p.name + ")" : label;
          })
          .join(", ");
        if (assigned.length > 5) {
          crossNamedList += ", +" + (assigned.length - 5) + " more";
        }
        this.setState({
          requesting: false,
          reqFlash: "",
          reqCrossServer:
            (data.message || `Assigned on ${data.server} — log in there to verify.`)
            + (crossNamedList ? " Patient(s): " + crossNamedList : ""),
          reqNotes: "",
        });
        return;
      }
      // Name the specific patient(s) assigned — device id + name — so the
      // verifier knows exactly what landed in their queue without having
      // to cross-reference the list below. Caps the listed names at 5 to
      // keep the banner readable on a larger batch request.
      var namedList = assigned
        .slice(0, 5)
        .map(function (p) {
          var label = p.device_id != null ? String(p.device_id) : String(p._id);
          return p.name ? label + " (" + p.name + ")" : label;
        })
        .join(", ");
      if (assigned.length > 5) {
        namedList += ", +" + (assigned.length - 5) + " more";
      }
      this.setState({
        requesting: false,
        reqFlash:
          (data.message || "Assigned")
          + " — " + assigned.length + " patient" + (assigned.length === 1 ? "" : "s")
          + " added to your queue: " + namedList,
        reqCrossServer: "",
        reqNotes: "",
      });
    } catch (err) {
      this.setState({
        requesting: false,
        reqCrossServer: "",
        reqError:
          (err.response && err.response.data && err.response.data.error)
          || err.message || "Request failed.",
      });
    }
  };

  componentWillReceiveProps(nextProps) {
    if (nextProps.errors) {
      this.setState({ errors: nextProps.errors });
    }
  }

  onChange = (e) => {
    const err = { ...this.state.errors };
    delete err[e.target.name];
    this.setState({ [e.target.name]: e.target.value, errors: err });
  };

  onSubmit = (e) => {
    e.preventDefault();
    this.setState({ saving: true });
    const user = {
      id: this.state.id,
      name: this.state.name,
      phone: this.state.phone,
      email: this.state.email,
      gender: this.state.gender,
      address: this.state.address,
    };
    this.props.updateProfile(user, this.props.history);
    // Optimistic feedback — the action is fire-and-forget and may navigate.
    setTimeout(() => {
      this.setState({ saving: false, savedAt: new Date() });
    }, 600);
  };

  render() {
    const { errors, name, email, phone, address, gender, saving, savedAt } =
      this.state;

    return (
      <div className="vp-page">
        <SideBar
          menus={
            localStorage.getItem("verifiertype") !== "7"
              ? this.state.menus
              : this.state.menu
          }
        />

        <div className="page-wrapper">
          <div className="vp-container">
            {/* Header — title + Log Out (visible regardless of sidebar
                layout / clipping). */}
            <div
              className="vp-header"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div>
                <h2>Profile</h2>
                <p>Attendance, verified activity and personal details</p>
              </div>
              <ProfileLogoutButton />
            </div>

            {/* ── Attendance & Verified Activity (primary) ────────── */}
            <div className="vp-card">
              <div className="vp-card__head">
                <h3>My Attendance & Verified Activity</h3>
                <span className="vp-card__hint">
                  Login / break / files & patients verified — Today, Week,
                  Month, or Custom range
                </span>
              </div>
              <AttendanceView selfOnly />
            </div>

            {/* ── Request files (self-service auto-pick) ─────────── */}
            <div className="vp-card">
              <div className="vp-card__head">
                <h3>Request file assignment</h3>
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  alignItems: "flex-start",
                }}
              >
                <label className="vp-field" style={{ minWidth: 120, maxWidth: 140 }}>
                  <span className="vp-field__label">How many?</span>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={this.state.reqCount}
                    onChange={(e) =>
                      this.setState({ reqCount: e.target.value })
                    }
                    className="vp-input"
                  />
                </label>

                <div className="vp-actions" style={{ paddingTop: 22 }}>
                  <button
                    type="button"
                    className="vp-btn"
                    onClick={this.requestFiles}
                    disabled={this.state.requesting}
                  >
                    {this.state.requesting ? "Requesting…" : "Request files"}
                  </button>
                </div>
              </div>

              {this.state.reqFlash && (
                <div
                  style={{
                    marginTop: 12,
                    padding: "8px 12px",
                    borderRadius: 6,
                    background: "rgba(34,197,94,0.10)",
                    border: "1px solid rgba(34,197,94,0.35)",
                    color: "#22c55e",
                    fontSize: 13,
                  }}
                >✓ {this.state.reqFlash}</div>
              )}
              {this.state.reqCrossServer && (
                <div
                  style={{
                    marginTop: 12,
                    padding: "10px 14px",
                    borderRadius: 6,
                    background: "rgba(234,179,8,0.12)",
                    border: "1px solid rgba(234,179,8,0.45)",
                    color: "#eab308",
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >🌐 {this.state.reqCrossServer}</div>
              )}
              {this.state.reqError && (
                <div
                  style={{
                    marginTop: 12,
                    padding: "8px 12px",
                    borderRadius: 6,
                    background: "rgba(239,68,68,0.10)",
                    border: "1px solid rgba(239,68,68,0.35)",
                    color: "#ff6b6b",
                    fontSize: 13,
                  }}
                >{this.state.reqError}</div>
              )}
            </div>

            {/* ── Today's verified patients ───────────────────────── */}
            <div className="vp-card">
              <div className="vp-card__head">
                <h3>Today's Verified Patients</h3>
                <span className="vp-card__hint">
                  {this.state.verifiedTodayLoading
                    ? "Loading…"
                    : this.state.verifiedToday.length
                      + " patient" + (this.state.verifiedToday.length === 1 ? "" : "s")
                      + " verified today"}
                  {" · "}
                  <button
                    type="button"
                    onClick={this.loadVerifiedToday}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#60a5fa",
                      cursor: "pointer",
                      padding: 0,
                      fontSize: "inherit",
                    }}
                  >↻ refresh</button>
                </span>
              </div>

              {this.state.verifiedTodayError && (
                <div
                  style={{
                    marginBottom: 12,
                    padding: "8px 12px",
                    borderRadius: 6,
                    background: "rgba(239,68,68,0.10)",
                    border: "1px solid rgba(239,68,68,0.35)",
                    color: "#ff6b6b",
                    fontSize: 13,
                  }}
                >{this.state.verifiedTodayError}</div>
              )}

              {!this.state.verifiedTodayLoading && !this.state.verifiedToday.length && !this.state.verifiedTodayError && (
                <div style={{ padding: "16px 0", opacity: 0.7, fontSize: 14 }}>
                  No patients verified today yet.
                </div>
              )}

              {this.state.verifiedToday.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr style={{ textAlign: "left", opacity: 0.75 }}>
                        <th style={{ padding: "8px 6px" }}>#</th>
                        <th style={{ padding: "8px 6px" }}>Device ID</th>
                        <th style={{ padding: "8px 6px" }}>Patient Name</th>
                        <th style={{ padding: "8px 6px" }}>Files Verified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {this.state.verifiedToday.map((r, i) => (
                        <tr
                          key={r.patientId || i}
                          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
                        >
                          <td style={{ padding: "8px 6px", opacity: 0.6 }}>{i + 1}</td>
                          <td style={{ padding: "8px 6px", fontFamily: "monospace" }}>
                            {r.deviceId != null
                              ? String(r.deviceId).padStart(6, "0")
                              : "—"}
                          </td>
                          <td style={{ padding: "8px 6px" }}>{r.name || "—"}</td>
                          <td style={{ padding: "8px 6px" }}>{r.fileCount || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: "1px solid rgba(255,255,255,0.12)", fontWeight: 600 }}>
                        <td style={{ padding: "8px 6px" }} colSpan={3}>
                          Total ({this.state.verifiedToday.length})
                        </td>
                        <td style={{ padding: "8px 6px" }}>
                          {this.state.verifiedToday.reduce((s, p) => s + (p.fileCount || 0), 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* ── Personal details (secondary, below attendance) ──── */}
            <div className="vp-card">
              <div className="vp-card__head">
                <h3>Personal Details</h3>
                {savedAt && (
                  <span className="vp-saved">
                    Saved {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </div>

              <form onSubmit={this.onSubmit} className="vp-form" autoComplete="off">
                <div className="vp-grid">
                  <label className="vp-field">
                    <span className="vp-field__label">Name</span>
                    <input
                      type="text"
                      name="name"
                      required
                      onChange={this.onChange}
                      value={name}
                      placeholder="Full name"
                      className={
                        "vp-input" + (errors.name ? " vp-input--error" : "")
                      }
                    />
                  </label>

                  <label className="vp-field">
                    <span className="vp-field__label">Email</span>
                    <input
                      type="email"
                      name="email"
                      required
                      onChange={this.onChange}
                      value={email}
                      placeholder="email@example.com"
                      className={
                        "vp-input" + (errors.email ? " vp-input--error" : "")
                      }
                    />
                  </label>

                  <label className="vp-field">
                    <span className="vp-field__label">Mobile</span>
                    <input
                      type="tel"
                      name="phone"
                      required
                      onChange={this.onChange}
                      value={phone}
                      placeholder="Phone"
                      className={
                        "vp-input" + (errors.phone ? " vp-input--error" : "")
                      }
                    />
                  </label>

                  <label className="vp-field">
                    <span className="vp-field__label">Gender</span>
                    <div className="vp-radio-row">
                      <label className="vp-radio">
                        <input
                          type="radio"
                          name="gender"
                          value="M"
                          checked={gender === "M"}
                          onChange={this.onChange}
                        />
                        <span>Male</span>
                      </label>
                      <label className="vp-radio">
                        <input
                          type="radio"
                          name="gender"
                          value="F"
                          checked={gender === "F"}
                          onChange={this.onChange}
                        />
                        <span>Female</span>
                      </label>
                    </div>
                  </label>

                  <label className="vp-field vp-field--wide">
                    <span className="vp-field__label">Address</span>
                    <textarea
                      name="address"
                      onChange={this.onChange}
                      value={address}
                      rows={3}
                      placeholder="Address"
                      className="vp-input vp-input--multiline"
                    />
                  </label>
                </div>

                <div className="vp-actions">
                  <button
                    type="submit"
                    className="vp-btn"
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

VerifierProfile.propTypes = {
  updateProfile: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  errors: state.errors,
});

export default connect(mapStateToProps, { updateProfile })(
  withRouter(VerifierProfile)
);
